/**
 * ImageUploader Component
 * Reusable component for uploading images with compression and preview
 */

import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { uploadImage, previewImage } from '../utils/imageUpload';

const ImageUploader = ({ 
  currentImageUrl = null,
  onImageUploaded,
  onImageRemoved,
  folder = 'products',
  label = 'Product Image',
  required = false
}) => {
  const [preview, setPreview] = useState(currentImageUrl);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [uploadedData, setUploadedData] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError(null);
    setUploading(true);
    setProgress({ stage: 'validating', progress: 0 });

    try {
      // Show preview immediately
      const previewUrl = await previewImage(file);
      setPreview(previewUrl);

      // Upload with progress tracking
      const result = await uploadImage(file, folder, (progressData) => {
        setProgress(progressData);
      });

      if (result.success) {
        console.log('✅ Image upload successful:', result);
        setUploadedData(result);
        setPreview(result.url);
        if (onImageUploaded) {
          console.log('📤 Calling onImageUploaded with URL:', result.url);
          onImageUploaded(result.url, result.path);
        }
      } else {
        console.error('❌ Image upload failed:', result.error);
        setError(result.error);
        setPreview(currentImageUrl); // Revert to original
      }
    } catch (err) {
      setError(err.message);
      setPreview(currentImageUrl);
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(null), 2000); // Clear progress after 2 seconds
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setUploadedData(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const getProgressStageText = (stage) => {
    switch (stage) {
      case 'validating': return 'Validating image...';
      case 'compressing': return 'Compressing image...';
      case 'compressed': return `Compressed to ${progress?.sizeKB}KB`;
      case 'uploading': return 'Uploading to cloud...';
      case 'complete': return 'Upload complete!';
      default: return 'Processing...';
    }
  };

  return (
    <div className="space-y-3">
      {/* Label */}
      <label className="block text-sm font-bold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Upload Area */}
      <div className="relative">
        {!preview ? (
          // Upload Prompt
          <div
            onClick={handleClick}
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-all"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Upload className="w-8 h-8 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">
                  Click to upload image
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPEG, PNG, or WebP (will be compressed to ~30KB)
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Image Preview
          <div className="relative border-2 border-gray-200 rounded-xl overflow-hidden">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-64 object-cover"
            />
            
            {/* Remove Button */}
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-3 right-3 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Change Image Button */}
            <button
              type="button"
              onClick={handleClick}
              className="absolute bottom-3 right-3 px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg shadow-lg transition-colors flex items-center gap-2"
            >
              <ImageIcon className="w-4 h-4" />
              Change Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Upload Info Badge - HIDDEN */}
            {uploadedData && false && (
              <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 shadow-lg">
                <CheckCircle className="w-4 h-4" />
                {uploadedData.sizeKB}KB
              </div>
            )}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {uploading && progress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-blue-900">
              {getProgressStageText(progress.stage)}
            </span>
            <span className="text-sm font-bold text-blue-600">
              {progress.progress}%
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-600 h-full transition-all duration-300 rounded-full"
              style={{ width: `${progress.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Success Message */}
      {!uploading && progress?.stage === 'complete' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-green-900">
            Image uploaded successfully! ({uploadedData?.sizeKB}KB)
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-900">Upload Failed</p>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Help Text - HIDDEN */}
      {false && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-600">
            <strong>Note:</strong> Images are automatically compressed to ~30KB to ensure fast loading. 
            For best results, use high-quality images (under 5MB) with simple backgrounds.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
